import { Chip } from "@outschool/backpack";
import {
  ListingPhoto_ActivityFragment,
  SectionRibbon_SectionFragment,
} from "@outschool/gql-frontend-generated";
import { TFunction, useTranslation } from "@outschool/localization";
import SectionUtils from "@outschool/section";
import { gql } from "@outschool/ui-apollo";
import { Banner } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import React from "react";

const Section = new SectionUtils();

enum BannerType {
  spotsLeft = "spots_left",
  isClub = "number_of_posts",
}

interface Banner {
  type: BannerType;
  text: string;
}

function getBannerForSection(
  t: TFunction,
  section: SectionRibbon_SectionFragment | null,
  activity: ListingPhoto_ActivityFragment,
): Banner | null {
  const numSpotsLeft = section
    ? Section.availableSpaces(section, section.filledSpaceCount)
    : 0;
  const offeredSpaceCount = section ? Section.offeredSpaces(section) : 0;
  const numPosts = activity.clubSection
    ? activity.clubSection.clubPostCount
    : 0;
  const numPostsFormatted = numPosts.toLocaleString();

  if (
    numSpotsLeft > 0 &&
    numSpotsLeft <= 3 &&
    numSpotsLeft < offeredSpaceCount
  ) {
    return {
      type: BannerType.spotsLeft,
      text: t("Selling fast"),
    };
  } else if (activity.isClub) {
    if (numPosts > 0) {
      return {
        type: BannerType.isClub,
        text: t("{{numPostsFormatted}} post in chat", {
          numPostsFormatted,
          count: numPosts,
          defaultValue_plural: "{{numPostsFormatted}} posts in chat",
        }),
      };
    }
  } else if (
    numSpotsLeft > 0 &&
    numSpotsLeft <= 3 &&
    numSpotsLeft < offeredSpaceCount
  ) {
    return {
      type: BannerType.spotsLeft,
      text: t("Only {{numSpotsLeft}} spot left", {
        numSpotsLeft,
        count: numSpotsLeft,
        defaultValue_plural: "Only {{numSpotsLeft}} spots left",
      }),
    };
  }
  return null;
}

interface AlertRibbonProps {
  section: SectionRibbon_SectionFragment | null;
  activity: ListingPhoto_ActivityFragment;
  showReviews?: boolean;
}

/**
 * Copy of the SectionRibbon component, but with Chip elements instead of a "ribbon".
 */
export default function Badges({ section, activity }: AlertRibbonProps) {
  const { t } = useTranslation(
    "client\\components\\search\\searchResultsGalleryExperiment\\AlertRibbon",
  );
  const banner = getBannerForSection(t, section, activity);
  const isMobile = useIsMobile();

  if (!banner) {
    return null;
  }

  return (
    <Chip
      color="warning"
      size="small"
      label={banner.text}
      sx={{
        paddingX: "1em",
        width: "fit-content",
        fontWeight: "fontWeightMedium",
        fontSize: isMobile ? "0.75em" : "0.875em",
        borderRadius: "0.25em",
      }}
    />
  );
}

// Hack to set the gql fragments of this component to the same as those of the SectionRibbon component.
Badges.fragments = {
  section: gql`
    fragment SectionRibbon_section on Section {
      size_max
      filledSpaceCount(exact: false)
    }
  `,
  activity: gql`
    fragment SectionRibbon_activity on Activity {
      clubSection {
        filledSpaceCount
        clubPostCount
      }
    }
  `,
};
